import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TrackingSettings {
  facebook?: { pixel_id: string; enabled: boolean };
  google?: { measurement_id: string; enabled: boolean };
  tiktok?: { pixel_id: string; enabled: boolean };
  x?: { pixel_id: string; enabled: boolean };
  linkedin?: { partner_id: string; enabled: boolean };
  seo?: { google_site_verification?: string; bing_site_verification?: string };
}

const TrackingPixels = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    const load = async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .in("key", [
          "tracking_facebook", "tracking_google", "tracking_tiktok",
          "tracking_x", "tracking_linkedin", "seo_settings",
        ]);

      if (!data) return;
      setLoaded(true);

      const settings: TrackingSettings = {};
      data.forEach((s: any) => {
        if (s.key === "tracking_facebook") settings.facebook = s.value;
        if (s.key === "tracking_google") settings.google = s.value;
        if (s.key === "tracking_tiktok") settings.tiktok = s.value;
        if (s.key === "tracking_x") settings.x = s.value;
        if (s.key === "tracking_linkedin") settings.linkedin = s.value;
        if (s.key === "seo_settings") settings.seo = s.value;
      });

      // Facebook Pixel
      if (settings.facebook?.enabled && settings.facebook.pixel_id) {
        const id = settings.facebook.pixel_id;
        const script = document.createElement("script");
        script.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');`;
        document.head.appendChild(script);
        const noscript = document.createElement("noscript");
        noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"/>`;
        document.head.appendChild(noscript);
      }

      // Google Analytics 4
      if (settings.google?.enabled && settings.google.measurement_id) {
        const id = settings.google.measurement_id;
        const gtagScript = document.createElement("script");
        gtagScript.async = true;
        gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        document.head.appendChild(gtagScript);
        const inlineScript = document.createElement("script");
        inlineScript.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`;
        document.head.appendChild(inlineScript);
      }

      // TikTok Pixel
      if (settings.tiktok?.enabled && settings.tiktok.pixel_id) {
        const id = settings.tiktok.pixel_id;
        const script = document.createElement("script");
        script.innerHTML = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${id}');ttq.page();}(window,document,'ttq');`;
        document.head.appendChild(script);
      }

      // X (Twitter) Pixel
      if (settings.x?.enabled && settings.x.pixel_id) {
        const id = settings.x.pixel_id;
        const script = document.createElement("script");
        script.innerHTML = `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');twq('config','${id}');`;
        document.head.appendChild(script);
      }

      // LinkedIn Insight Tag
      if (settings.linkedin?.enabled && settings.linkedin.partner_id) {
        const id = settings.linkedin.partner_id;
        const script = document.createElement("script");
        script.innerHTML = `_linkedin_partner_id="${id}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];var b=document.createElement("script");b.type="text/javascript";b.async=true;b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";s.parentNode.insertBefore(b,s);})(window.lintrk);`;
        document.head.appendChild(script);
      }

      // SEO verification meta tags
      if (settings.seo?.google_site_verification) {
        const meta = document.createElement("meta");
        meta.name = "google-site-verification";
        meta.content = settings.seo.google_site_verification;
        document.head.appendChild(meta);
      }
      if (settings.seo?.bing_site_verification) {
        const meta = document.createElement("meta");
        meta.name = "msvalidate.01";
        meta.content = settings.seo.bing_site_verification;
        document.head.appendChild(meta);
      }
    };

    load();
  }, [loaded]);

  return null;
};

export default TrackingPixels;
